import { useRef, useState } from 'react';

interface Props {
  onFiles: (files: File[]) => void;
  accept?: string;
  maxMB?: number;
  multiple?: boolean;
}

export default function FileUpload({
  onFiles,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  maxMB = 25,
  multiple = true,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(files: File[]): File[] | null {
    const tooBig = files.filter((f) => f.size > maxMB * 1024 * 1024);
    if (tooBig.length > 0) {
      setError(`${tooBig.map((f) => f.name).join(', ')} exceeds ${maxMB}MB limit`);
      return null;
    }
    setError(null);
    return files;
  }

  function handle(files: FileList | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files);
    const valid = validate(arr);
    if (valid) onFiles(valid);
  }

  return (
    <div>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files); }}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <p className="text-sm text-gray-500">
          Drag & drop files here, or{' '}
          <span className="text-blue-600 font-medium">browse</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, XLSX, JPG, PNG — max {maxMB}MB per file</p>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => handle(e.target.files)}
      />
    </div>
  );
}
